import React from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema, ServiceSchema } from "../components/SEO";
import { Link } from "react-router-dom";
import { FadeUp } from "../components/Motion";
import KitchenBathGallery from "../components/KitchenBathGallery";
import AreasWeServe from "../components/AreasWeServe";
import CustomHomeBuildGallery from "../components/CustomHomeBuildGallery";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG = {
  "renovations": "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/hc9pzs46_After%202.jpeg",
  "kitchen-and-bath": "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/q7mitknb_after%203.jpeg",
  "commercial-construction": "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/ci6ibh3r_image.png",
  "custom-homes": null,  // Plain dark hero per instructions
};

const SHOW_AREAS = new Set(["custom-homes", "renovations", "kitchen-and-bath"]);

const CONTENT = {
  "custom-homes": {
    seoTitle: "Custom Home Builder in Tampa, FL",
    seoDesc: "Architect-coordinated custom homes in South Tampa and across Tampa Bay, built by an engineer-led, veteran-owned general contractor. CGC1515086.",
    eyebrow: "Construction",
    h1: "Custom Homes in Tampa",
    sub: "Architect-designed homes, planned and built with engineering discipline.",
    para: "We build custom homes across Tampa Bay, working alongside your architect and engineer from pricing through closeout. Our job is to protect the design, the budget and the schedule, and to solve problems on paper before they reach the field. You work directly with the people managing your home, start to finish.",
    serviceType: "Custom home construction",
    points: [
      { t: "Preconstruction", d: "Scope, budget and design aligned before ground breaks." },
      { t: "Design coordination", d: "We work with your architect, engineer and designer, or help you find the right ones. Design-build is available." },
      { t: "Craft and finish", d: "Millwork, trim and detailing held to one standard." },
      { t: "Built for Florida", d: "Materials and details chosen for heat, humidity, wind and water." },
    ],
    ctaH2: "Planning a custom home?",
  },
  "renovations": {
    seoTitle: "Whole-Home Renovations & Additions in Tampa",
    seoDesc: "Whole-home renovations, additions and in-law suites across Tampa Bay, with clear line-item pricing and permits handled for you.",
    eyebrow: "Construction",
    h1: "Whole-Home Renovations & Additions in Tampa",
    sub: "Major renovations and additions, planned so you can live through them.",
    para: "Renovating an occupied home takes planning: phasing, protection, permits and clear communication. We manage the schedule, the trades and the inspections so the house stays livable and the project stays on track.",
    serviceType: "Whole-home renovation and additions",
    points: [
      { t: "Whole-home renovations", d: "Interiors reworked with a phasing plan that limits disruption." },
      { t: "Additions and in-law suites", d: "New space that matches the original home in structure, systems and finish." },
      { t: "Permits and inspections", d: "Tampa, Hillsborough County and Pasco County permitting handled for you." },
      { t: "Clear pricing", d: "Line-item estimates with allowances shown before work starts." },
    ],
    featured: { slug: "waverly-park-kitchen", title: "Waverly Park Kitchen", loc: "South Tampa", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/gfxrb2yy_After%201.jpeg" },
    ctaH2: "Planning a renovation?",
  },
  "kitchen-and-bath": {
    seoTitle: "Kitchen & Bath Remodeling in Tampa",
    seoDesc: "Tampa kitchen and bathroom remodels planned from the inside out: layout, rough-in, waterproofing, cabinetry and tile, with itemized pricing.",
    eyebrow: "Construction",
    h1: "Kitchen & Bath Remodeling in Tampa",
    sub: "Kitchens and baths rebuilt the right way, behind the walls and on the surface.",
    para: "Most remodel problems start where you can't see them: plumbing rough-in, waterproofing, venting and layouts that ignore appliance clearances. We plan those details first, coordinate licensed trades, and handle permits and inspections, so the finished room looks right and holds up.",
    serviceType: "Kitchen and bath remodeling",
    points: [
      { t: "Kitchens", d: "Layout, cabinetry, counters, lighting and appliance packages." },
      { t: "Primary baths and showers", d: "Waterproofing, tile, walk-in showers, soaking tubs and vanities." },
      { t: "Cabinet sales and installation", d: "Semi-custom and custom cabinetry, measured and installed by the team that owns the scope." },
      { t: "Transparent pricing", d: "Cabinet, countertop, tile and fixture allowances itemized up front." },
    ],
    gallery: true,
    featured: { slug: "waverly-park-kitchen", title: "Waverly Park Kitchen", loc: "South Tampa", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/gfxrb2yy_After%201.jpeg" },
    ctaH2: "Planning a kitchen or bath remodel?",
  },
  "commercial-construction": {
    seoTitle: "Commercial Contractor in Tampa & Florida",
    seoDesc: "Ground-up commercial, franchise retail and healthcare build-outs in Florida, including the Goddard School in Tampa and 20+ Hair Cuttery salons.",
    eyebrow: "Construction",
    h1: "Commercial Construction in Tampa and Across Florida",
    sub: "Retail, franchise, institutional and healthcare build-outs.",
    para: "We deliver ground-up commercial buildings and tenant build-outs on tight schedules and to national brand standards. Past work includes the three-story Goddard School on Kennedy Boulevard in Tampa and more than twenty Hair Cuttery salons statewide under a Master Construction Contract with Ratner Companies.",
    serviceType: "Commercial construction and franchise retail build-outs",
    points: [
      { t: "Ground-up commercial", d: "New construction with steel and masonry systems." },
      { t: "Franchise and retail build-outs", d: "55 to 90 day turnovers to national brand standards." },
      { t: "Healthcare and institutional", d: "Renovations inside occupied VA medical centers, phased around patients and staff." },
      { t: "Owner representation", d: "Design, permitting, construction and opening managed on the owner's behalf." },
    ],
    selectedWork: [
      { slug: "goddard-school-kennedy-tampa", title: "Goddard School", loc: "Kennedy Boulevard, Tampa", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/ci6ibh3r_image.png" },
      { slug: "hair-cuttery-retail-program", title: "Hair Cuttery Build-Out Program", loc: "20+ locations, Florida", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/emonanyr_image.webp" },
    ],
    ctaH2: "Planning a commercial build-out?",
  },
};

export default function ServicePage({ slug }) {
  const data = CONTENT[slug];
  if (!data) return null;

  return (
    <>
      <SEO title={data.seoTitle} description={data.seoDesc} path={`/${slug}`} />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: data.h1, url: `https://bcpeabody.com/${slug}` }]} />
      <ServiceSchema serviceType={data.serviceType} path={`/${slug}`} />
      <Nav transparentOnTop />

      {/* Hero */}
      <section data-testid={`service-hero-${slug}`} className="relative min-h-[55vh] flex items-end overflow-hidden bg-ink text-cream-light">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-copper/10 blur-[160px]" />
        </div>
        {HERO_IMG[slug] && (
          <>
            <img
              src={HERO_IMG[slug]}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(46,41,35,0.92) 0%, rgba(46,41,35,0.78) 55%, rgba(46,41,35,0.55) 100%)" }} />
          </>
        )}
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 w-full pb-14 pt-32">
          <div className="text-[11px] tracking-[0.18em] uppercase text-copper-light mb-5">{data.eyebrow}</div>
          <h1 className="font-serif font-medium tracking-tight mb-5 leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 60px)" }}>{data.h1}</h1>
          <p className="text-cream-light/90 max-w-3xl" style={{ fontSize: 18 }}>{data.sub}</p>
        </div>
      </section>

      {/* Intro paragraph */}
      <section className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="text-ink/85 leading-relaxed" style={{ fontSize: 18 }}>{data.para}</p>
          </FadeUp>
        </div>
      </section>

      {/* Four points */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {data.points.map((p, i) => (
            <FadeUp key={p.t} delay={i * 60}>
              <div className="border-t border-copper pt-5">
                <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-3">0{i+1}</div>
                <h3 className="font-serif text-2xl text-ink mb-3 tracking-tight">{p.t}</h3>
                <p className="text-ink/75 leading-relaxed">{p.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Kitchen & Bath gallery */}
      {data.gallery && <KitchenBathGallery />}

      {/* Custom home ground-up gallery */}
      {slug === "custom-homes" && <CustomHomeBuildGallery />}

      {slug === "custom-homes" && (
        <section data-testid="custom-homes-featured-north-tampa" className="bg-cream-light pb-16 md:pb-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-6">Featured project</div>
            <Link to="/projects/north-tampa-custom-home" className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
              <div className="md:col-span-7 overflow-hidden bg-ink aspect-[4/3]">
                <img src="/images/projects/bcpeabody-north-tampa-custom-home-primary-bath-07.jpg" alt="Primary bathroom with freestanding tub, backlit onyx column and marble walls, North Tampa custom home" loading="lazy" className="w-full h-full object-cover object-[center_60%] group-hover:scale-[1.02] transition-transform duration-[1200ms]" />
              </div>
              <div className="md:col-span-5">
                <h3 className="font-serif text-3xl text-ink tracking-tight mb-3">North Tampa Custom Home</h3>
                <p className="text-ink/75 leading-relaxed mb-5">A single-story custom home with a screened pool, outdoor kitchen and groin-vault dining room.</p>
                <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-copper">See the finished home <ArrowUpRight size={14} /></span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Featured project */}
      {data.featured && (
        <section className="bg-cream-light py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-6">Featured project</div>
            <Link to={`/projects/${data.featured.slug}`} className="group block">
              <div className="relative h-[380px] md:h-[480px] overflow-hidden bg-ink mb-5">
                <img src={data.featured.img} alt={`${data.featured.title}, ${data.featured.loc}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
              </div>
              <h3 className="font-serif text-3xl text-ink tracking-tight mb-1">{data.featured.title}</h3>
              <p className="text-ink/60 text-sm">{data.featured.loc}</p>
            </Link>
          </div>
        </section>
      )}

      {/* Selected work (commercial) */}
      {data.selectedWork && (
        <section className="bg-cream-light py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-6">Selected work</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {data.selectedWork.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} className="group block">
                  <div className="relative h-[320px] overflow-hidden bg-ink mb-4">
                    <img src={p.img} alt={`${p.title}, ${p.loc}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                  </div>
                  <h3 className="font-serif text-2xl text-ink tracking-tight mb-1">{p.title}</h3>
                  <p className="text-ink/60 text-sm">{p.loc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Areas we serve (residential pages only) */}
      {SHOW_AREAS.has(slug) && <AreasWeServe />}

      {/* CTA band */}
      <section className="bg-ink text-cream-light py-20 text-center">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="font-serif font-medium mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3.2vw, 36px)" }}>{data.ctaH2}</h2>
          <Link to="/contact" className="btn-copper">Start a Project →</Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
